/**
 * Photo picker utility for embedded mode
 * Handles communication with Android WebView for native photo picker
 * Includes video conversion for cross-platform compatibility
 */

import { processVideoFile } from './videoConverter';

export interface PhotoPickerResult {
  success: boolean;
  imageData?: string; // Base64 encoded image data
  latitude?: number;
  longitude?: number;
  captureTime?: string;
  error?: string;
}

export interface MediaPickerResult {
  success: boolean;
  mediaData?: string; // Base64 encoded media data
  mediaType?: 'image' | 'video';
  latitude?: number;
  longitude?: number;
  captureTime?: string;
  error?: string;
}

/**
 * Open the native media picker (photos and videos)
 * This will show the native media picker that allows users to:
 * - Select from gallery
 * - Take a new photo
 * - Record a new video (if allowed)
 * 
 * @param options Configuration options for the media picker
 * @returns Promise<MediaPickerResult>
 */
export const openMediaPicker = (options: { allowVideo?: boolean } = {}): Promise<MediaPickerResult> => {
  const { allowVideo = true } = options;
  return new Promise((resolve) => {
    console.log('📱 [PhotoPicker] openMediaPicker() called');
    console.log('📱 [PhotoPicker] window object:', typeof window);
    console.log('📱 [PhotoPicker] ReactNativeWebView exists:', typeof (window as any).ReactNativeWebView !== 'undefined');
    
    // Check if we're in embedded mode (Android WebView)
    if (typeof (window as any).ReactNativeWebView !== 'undefined') {
      console.log('📱 [PhotoPicker] ReactNativeWebView object:', (window as any).ReactNativeWebView);
      console.log('📱 [PhotoPicker] ReactNativeWebView.openMediaPicker exists:', typeof (window as any).ReactNativeWebView.openMediaPicker === 'function');
      console.log('📱 [PhotoPicker] Available ReactNativeWebView methods:', Object.keys((window as any).ReactNativeWebView));
      
      // Set up callback for when media is captured (using existing onPhotoCaptured mechanism)
      (window as any).onPhotoCaptured = function(base64Data: string, latitude?: number, longitude?: number, captureTime?: string) {
        console.log('📱 [PhotoPicker] onPhotoCaptured callback triggered with base64 data length:', base64Data?.length);
        resolve({ success: true, mediaData: base64Data, mediaType: 'image', latitude, longitude, captureTime });
        
        // Clean up the callback
        delete (window as any).onPhotoCaptured;
      };

      // Set up callback for when video is captured
      (window as any).onVideoCaptured = function(base64Data: string, latitude?: number, longitude?: number, captureTime?: string) {
        console.log('📱 [PhotoPicker] onVideoCaptured callback triggered with base64 data length:', base64Data?.length);
        resolve({ success: true, mediaData: base64Data, mediaType: 'video', latitude, longitude, captureTime });
        
        // Clean up the callback
        delete (window as any).onVideoCaptured;
      };
      
      // Set up callback for when media is captured (using new onMediaCaptured mechanism)
      (window as any).onMediaCaptured = function(base64Data: string, mediaType: string, latitude?: number, longitude?: number, captureTime?: string) {
        console.log('📱 [PhotoPicker] onMediaCaptured callback triggered with base64 data length:', base64Data?.length, 'type:', mediaType);
        resolve({ success: true, mediaData: base64Data, mediaType: mediaType as 'image' | 'video', latitude, longitude, captureTime });
        
        // Clean up the callback
        delete (window as any).onMediaCaptured;
      };
      
      // Set up callback for when media picker is cancelled
      (window as any).onPhotoPickerCancelled = function() {
        console.log('📱 [PhotoPicker] onPhotoPickerCancelled callback triggered');
        resolve({ success: false, error: 'User cancelled media picker' });
        
        // Clean up the callback
        delete (window as any).onPhotoPickerCancelled;
      };
      
      console.log('📱 [PhotoPicker] Callbacks set up, calling ReactNativeWebView.openMediaPicker()');
      
      // Call the native interface - try iOS first, then Android
      if (typeof (window as any).ReactNativeWebView.openMediaPicker === 'function') {
        try {
          // iOS: Use the existing openMediaPicker function with video permissions
          console.log('📱 [PhotoPicker] Calling iOS openMediaPicker with allowVideo:', allowVideo);
          (window as any).ReactNativeWebView.openMediaPicker(allowVideo);
          console.log('📱 [PhotoPicker] ReactNativeWebView.openMediaPicker() called (iOS) with allowVideo:', allowVideo);
        } catch (error) {
          console.error('📱 [PhotoPicker] Error calling ReactNativeWebView.openMediaPicker():', error);
          resolve({ success: false, error: `Error calling media picker: ${error instanceof Error ? error.message : 'Unknown error'}` });
        }
      } else if (typeof (window as any).ReactNativeWebView.postMessage === 'function') {
        try {
          // Android: Use postMessage with video permissions
          (window as any).ReactNativeWebView.postMessage(JSON.stringify({
            type: 'OPEN_MEDIA_PICKER',
            data: { allowVideo }
          }));
          console.log('📱 [PhotoPicker] ReactNativeWebView.postMessage() called with allowVideo:', allowVideo);
        } catch (error) {
          console.error('📱 [PhotoPicker] Error calling ReactNativeWebView.postMessage():', error);
          resolve({ success: false, error: `Error calling media picker: ${error instanceof Error ? error.message : 'Unknown error'}` });
        }
      } else if (typeof (window as any).ReactNativeWebView.captureMedia === 'function') {
        try {
          console.log('📱 [PhotoPicker] Using existing captureMedia method with allowVideo:', allowVideo);
          // Pass video permissions to the native interface
          (window as any).ReactNativeWebView.postMessage(JSON.stringify({
            type: 'CAPTURE_MEDIA',
            data: { allowVideo }
          }));
          console.log('📱 [PhotoPicker] ReactNativeWebView.captureMedia() called successfully');
        } catch (error) {
          console.error('📱 [PhotoPicker] Error calling ReactNativeWebView.captureMedia():', error);
          resolve({ success: false, error: `Error calling media capture: ${error instanceof Error ? error.message : 'Unknown error'}` });
        }
      } else {
        console.warn('📱 [PhotoPicker] Neither openMediaPicker nor captureMedia available');
        console.log('📱 [PhotoPicker] Available methods:', Object.keys((window as any).ReactNativeWebView));
        resolve({ success: false, error: 'Media picker not available' });
      }
    } else {
      // Not in embedded mode, fallback to web file input
      console.log('📱 [PhotoPicker] Not in embedded mode, using web fallback');
      console.log('📱 [PhotoPicker] User agent:', navigator.userAgent);
      resolve({ success: false, error: 'Not in embedded mode' });
    }
  });
};

/**
 * Open the native Android photo picker
 * This will show the native Android photo picker that allows users to:
 * - Select from gallery
 * - Take a new photo
 * 
 * @returns Promise<PhotoPickerResult>
 */
export const openPhotoPicker = (): Promise<PhotoPickerResult> => {
  return new Promise((resolve) => {
    console.log('📱 [PhotoPicker] openPhotoPicker() called');
    console.log('📱 [PhotoPicker] window object:', typeof window);
    console.log('📱 [PhotoPicker] ReactNativeWebView exists:', typeof (window as any).ReactNativeWebView !== 'undefined');
    
    // Check if we're in embedded mode (Android WebView)
    if (typeof (window as any).ReactNativeWebView !== 'undefined') {
      console.log('📱 [PhotoPicker] ReactNativeWebView object:', (window as any).ReactNativeWebView);
      console.log('📱 [PhotoPicker] ReactNativeWebView.openPhotoPicker exists:', typeof (window as any).ReactNativeWebView.openPhotoPicker === 'function');
      console.log('📱 [PhotoPicker] Available ReactNativeWebView methods:', Object.keys((window as any).ReactNativeWebView));
      
      // Set up callback for when photo is captured (using existing onPhotoCaptured mechanism)
      (window as any).onPhotoCaptured = function(base64Data: string, latitude?: number, longitude?: number, captureTime?: string) {
        console.log('📱 [PhotoPicker] onPhotoCaptured callback triggered with base64 data length:', base64Data?.length);
        resolve({ success: true, imageData: base64Data, latitude, longitude, captureTime });
        
        // Clean up the callback
        delete (window as any).onPhotoCaptured;
      };
      
      // Set up callback for when photo is captured (using sendPhotoData mechanism from Android)
      (window as any).ReactNativeWebView.sendPhotoData = function(base64Data: string, latitude?: number, longitude?: number, captureTime?: string) {
        console.log('📱 [PhotoPicker] sendPhotoData callback triggered with base64 data length:', base64Data?.length);
        resolve({ success: true, imageData: base64Data, latitude, longitude, captureTime });
        
        // Clean up the callback
        delete (window as any).ReactNativeWebView.sendPhotoData;
      };
      
      // Set up callback for when photo is selected (alternative callback)
      (window as any).onPhotoSelected = function(result: PhotoPickerResult) {
        console.log('📱 [PhotoPicker] onPhotoSelected callback triggered with result:', result);
        resolve(result);
        
        // Clean up the callback
        delete (window as any).onPhotoSelected;
      };
      
      // Set up callback for when photo picker is cancelled
      (window as any).onPhotoPickerCancelled = function() {
        console.log('📱 [PhotoPicker] onPhotoPickerCancelled callback triggered');
        resolve({ success: false, error: 'User cancelled photo picker' });
        
        // Clean up the callback
        delete (window as any).onPhotoPickerCancelled;
      };
      
      console.log('📱 [PhotoPicker] Callbacks set up, calling ReactNativeWebView.openPhotoPicker()');
      
      // Call the Android JavaScript interface
      if (typeof (window as any).ReactNativeWebView.openPhotoPicker === 'function') {
        try {
          (window as any).ReactNativeWebView.openPhotoPicker();
          console.log('📱 [PhotoPicker] ReactNativeWebView.openPhotoPicker() called successfully');
        } catch (error) {
          console.error('📱 [PhotoPicker] Error calling ReactNativeWebView.openPhotoPicker():', error);
          resolve({ success: false, error: `Error calling photo picker: ${error instanceof Error ? error.message : 'Unknown error'}` });
        }
      } else if (typeof (window as any).ReactNativeWebView.capturePhoto === 'function') {
        try {
          console.log('📱 [PhotoPicker] Using existing capturePhoto method');
          (window as any).ReactNativeWebView.capturePhoto();
          console.log('📱 [PhotoPicker] ReactNativeWebView.capturePhoto() called successfully');
        } catch (error) {
          console.error('📱 [PhotoPicker] Error calling ReactNativeWebView.capturePhoto():', error);
          resolve({ success: false, error: `Error calling photo capture: ${error instanceof Error ? error.message : 'Unknown error'}` });
        }
      } else {
        console.warn('📱 [PhotoPicker] Neither openPhotoPicker nor capturePhoto available');
        console.log('📱 [PhotoPicker] Available methods:', Object.keys((window as any).ReactNativeWebView));
        resolve({ success: false, error: 'Photo picker not available' });
      }
    } else {
      // Not in embedded mode, fallback to web file input
      console.log('📱 [PhotoPicker] Not in embedded mode, using web fallback');
      console.log('📱 [PhotoPicker] User agent:', navigator.userAgent);
      resolve({ success: false, error: 'Not in embedded mode' });
    }
  });
};

/**
 * Set up persistent callbacks for multi-media selection (iOS PHPicker)
 * Unlike openMediaPicker which resolves after the first item, this keeps
 * callbacks alive to handle multiple items selected at once.
 * 
 * @param onMediaReceived - Called for each media item received
 * @param onComplete - Called when all items have been processed
 * @returns Cleanup function to remove callbacks
 */
export const setupMultiMediaCallback = (
  onMediaReceived: (base64Data: string, mediaType: 'image' | 'video', latitude?: number, longitude?: number, captureTime?: string) => void,
  onComplete: () => void
): (() => void) => {
  console.log('📱 [PhotoPicker] Setting up multi-media callback');
  
  // Set up persistent callback that doesn't delete itself after first call
  (window as any).onPhotoCaptured = function(base64Data: string, latitude?: number, longitude?: number, captureTime?: string) {
    console.log('📱 [PhotoPicker] onPhotoCaptured (multi) triggered with base64 data length:', base64Data?.length);
    onMediaReceived(base64Data, 'image', latitude, longitude, captureTime);
  };

  (window as any).onMediaCaptured = function(base64Data: string, mediaType: string, latitude?: number, longitude?: number, captureTime?: string) {
    console.log('📱 [PhotoPicker] onMediaCaptured (multi) triggered with base64 data length:', base64Data?.length, 'type:', mediaType);
    onMediaReceived(base64Data, mediaType as 'image' | 'video', latitude, longitude, captureTime);
  };

  (window as any).onMultiMediaComplete = function() {
    console.log('📱 [PhotoPicker] onMultiMediaComplete triggered');
    onComplete();
  };

  // Return cleanup function
  return () => {
    console.log('📱 [PhotoPicker] Cleaning up multi-media callbacks');
    delete (window as any).onPhotoCaptured;
    delete (window as any).onMediaCaptured;
    delete (window as any).onMultiMediaComplete;
  };
};

/**
 * Trigger the native media picker for multi-select
 * This should be called after setupMultiMediaCallback
 */
export const triggerNativeMediaPicker = (allowVideo: boolean = true): void => {
  console.log('📱 [PhotoPicker] Triggering native media picker with allowVideo:', allowVideo);
  
  if (typeof (window as any).ReactNativeWebView?.openMediaPicker === 'function') {
    (window as any).ReactNativeWebView.openMediaPicker(allowVideo);
  } else {
    console.warn('📱 [PhotoPicker] openMediaPicker not available');
  }
};

/**
 * Check if native photo picker is available
 */
export const isPhotoPickerAvailable = (): boolean => {
  const hasWebView = typeof (window as any).ReactNativeWebView !== 'undefined';
  const hasPhotoPicker = typeof (window as any).ReactNativeWebView?.openPhotoPicker === 'function';
  const hasCapturePhoto = typeof (window as any).ReactNativeWebView?.capturePhoto === 'function';
  
  console.log('📱 [PhotoPicker] isPhotoPickerAvailable() called');
  console.log('📱 [PhotoPicker] hasWebView:', hasWebView);
  console.log('📱 [PhotoPicker] hasPhotoPicker:', hasPhotoPicker);
  console.log('📱 [PhotoPicker] hasCapturePhoto:', hasCapturePhoto);
  console.log('📱 [PhotoPicker] ReactNativeWebView methods:', hasWebView ? Object.keys((window as any).ReactNativeWebView) : 'N/A');
  
  // Use either openPhotoPicker or capturePhoto
  return hasWebView && (hasPhotoPicker || hasCapturePhoto);
};

/**
 * Convert base64 media data to File object with automatic video conversion
 * This is used to convert the media data received from native apps to a File object
 * that can be used with the existing upload logic. Videos are automatically converted to MP4.
 */
export const base64ToFile = async (base64Data: string, filename: string, mediaType: 'image' | 'video' = 'image'): Promise<File> => {
  // Remove data URL prefix if present
  const base64 = base64Data.replace(/^data:(image|video)\/[a-z]+;base64,/, '');
  
  // Convert base64 to binary
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  
  const byteArray = new Uint8Array(byteNumbers);
  
  // Determine MIME type based on media type and filename
  let mimeType: string;
  if (mediaType === 'video') {
    // Check file extension to determine correct MIME type
    if (filename.toLowerCase().endsWith('.mov')) {
      mimeType = 'video/quicktime';
    } else if (filename.toLowerCase().endsWith('.avi')) {
      mimeType = 'video/avi';
    } else {
      mimeType = 'video/mp4'; // Default to mp4
    }
  } else {
    // For images, check file extension
    if (filename.toLowerCase().endsWith('.png')) {
      mimeType = 'image/png';
    } else if (filename.toLowerCase().endsWith('.gif')) {
      mimeType = 'image/gif';
    } else if (filename.toLowerCase().endsWith('.webp')) {
      mimeType = 'image/webp';
    } else {
      mimeType = 'image/jpeg'; // Default to jpeg
    }
  }
  
  // Create File object
  const file = new File([byteArray], filename, { type: mimeType });
  
  console.log('📁 [base64ToFile] Created file:', {
    name: file.name,
    size: file.size,
    type: file.type,
    mediaType,
    filename
  });
  
  // If it's a video, process it for conversion
  if (mediaType === 'video') {
    console.log('🎬 [base64ToFile] Processing video for conversion...');
    const conversionResult = await processVideoFile(file);
    
    if (conversionResult.success && conversionResult.file) {
      console.log('✅ [base64ToFile] Video conversion successful:', {
        originalName: file.name,
        convertedName: conversionResult.file.name,
        originalSize: file.size,
        convertedSize: conversionResult.file.size
      });
      return conversionResult.file;
    } else {
      console.warn('⚠️ [base64ToFile] Video conversion failed, using original file:', conversionResult.error);
      return file;
    }
  }
  
  return file;
};
